import React, { useState, useEffect, useRef } from "react";
import { TextField, IconButton, InputAdornment, Typography } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Cta } from '../components/Cta.js';
import { ReactComponent as Check } from "../assets/loginPage/check.svg";
import { ReactComponent as Briefcase } from "../assets/loginPage/briefcase.svg";
import { ReactComponent as Group31 } from "../assets/loginPage/group-31.svg";
import { ReactComponent as Mail } from "../assets/loginPage/mail.svg";
import maskGroup from "../assets/loginPage/mask-group.png";
import "../css/AuthPages.css";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../firebase';
import { applyActionCode, checkActionCode, confirmPasswordReset, signOut } from "firebase/auth";
import { createPopup } from '../utils/popupUtils';
import { StyledEngineProvider } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";

/*
This page is the project's custom Firebase email action handler — the "action
URL" set on the email templates in the Firebase console. Firebase appends
mode, oobCode, apiKey and (optionally) continueUrl + lang to the URL.

It dispatches on every operation Firebase can send here, not just password
resets: the console's action URL may be shared across templates, so
verification links can land here too. Those are applied and handed back to
/login?verified=..., which LogPage already reports on.

The operation comes from checkActionCode, not from the `mode` query param —
`mode` is user-editable, and verifyPasswordResetCode would happily accept a
verification code and hand back an email, leaving us rendering a password form
that can only fail on submit.
*/

// Matches the minimum configured in the Firebase console (Authentication →
// Settings → Password policy). Raise both together.
const MIN_PASSWORD_LENGTH = 6;

// Codes that can come back from any call that takes an oobCode.
const ACTION_CODE_MESSAGES = {
    'auth/expired-action-code': 'This password reset link has expired. Please request a new one.',
    'auth/invalid-action-code': 'This link is invalid or has already been used. Only the most recent link works — please request a new one.',
    'auth/user-not-found': 'This link is no longer valid. Please request a new one.',
    'auth/user-disabled': 'This account has been disabled. Please contact support.',
    'auth/too-many-requests': 'Too many requests — please wait a few minutes before trying again.',
    'auth/argument-error': 'This link is incomplete. Please use the link from your email, or request a new one.',
    'auth/network-request-failed': 'Network error. Please check your connection and try again.',
};

const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [status, setStatus] = useState('checking'); // 'checking' | 'ready' | 'invalid'
    const [invalidMessage, setInvalidMessage] = useState('');
    const [accountEmail, setAccountEmail] = useState('');
    const [oobCode, setOobCode] = useState('');
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // The action code is single-use and applyActionCode consumes it. StrictMode
    // runs effects twice in dev, so without this guard the second run burns the
    // code and a good link looks broken.
    const hasHandledCode = useRef(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
        setValidationError('');
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    useEffect(() => {
        if (hasHandledCode.current) {
            return;
        }
        hasHandledCode.current = true;

        const code = searchParams.get("oobCode");

        if (!code) {
            setInvalidMessage("This link is incomplete. Please use the link from your email, or request a new one.");
            setStatus('invalid');
            return;
        }

        const handleCode = async () => {
            try {
                // checkActionCode validates the code without consuming it, so it
                // is safe to call on mount and still confirm the reset on submit.
                const info = await checkActionCode(auth, code);

                // Verification / email-recovery links that land here are applied
                // and handed back to the login page, which already reports both
                // outcomes via ?verified=.
                if (info.operation === 'VERIFY_EMAIL' || info.operation === 'VERIFY_AND_CHANGE_EMAIL') {
                    try {
                        await applyActionCode(auth, code);
                        navigate('/login?verified=true');
                    } catch (applyError) {
                        console.error('Apply action code error:', applyError);
                        navigate('/login?verified=false');
                    }
                    return;
                }

                if (info.operation === 'RECOVER_EMAIL') {
                    try {
                        await applyActionCode(auth, code);
                        createPopup("Your email address has been restored. Please log in.", 'success', { gradient: true, duration: 6000 });
                    } catch (applyError) {
                        console.error('Apply action code error:', applyError);
                        createPopup("This link is invalid or has already been used.", 'error', { gradient: true, duration: 4000 });
                    }
                    navigate('/login');
                    return;
                }

                if (info.operation !== 'PASSWORD_RESET') {
                    setInvalidMessage("This isn't a password reset link. Please request a new one.");
                    setStatus('invalid');
                    return;
                }

                setOobCode(code);
                setAccountEmail(info.data.email || '');
                setStatus('ready');
            } catch (error) {
                console.error('Action code check error:', error);
                setInvalidMessage(
                    ACTION_CODE_MESSAGES[error.code] || "This link is invalid or has expired. Please request a new one."
                );
                setStatus('invalid');
            }
        };

        handleCode();
    }, [searchParams, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.password.length < MIN_PASSWORD_LENGTH) {
            setValidationError(`Password should be at least ${MIN_PASSWORD_LENGTH} characters.`);
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setValidationError("The two passwords don't match.");
            return;
        }

        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);

        try {
            await confirmPasswordReset(auth, oobCode, formData.password);

            // confirmPasswordReset never signs the user in, and any session open
            // in this browser is now holding a revoked refresh token. Clear it so
            // they re-authenticate cleanly through LogPage, which handles the
            // "no UserInformation doc yet" case.
            try {
                await signOut(auth);
            } catch (signOutError) {
                console.error('Sign out after reset failed:', signOutError);
            }

            createPopup("Your password has been reset. Please log in with your new password.", 'success', { gradient: true, duration: 6000 });
            navigate('/login');
        } catch (error) {
            console.error('Confirm password reset error:', error);

            const friendlyMessages = {
                ...ACTION_CODE_MESSAGES,
                'auth/weak-password': `Password should be at least ${MIN_PASSWORD_LENGTH} characters.`,
                'auth/missing-password': 'Please enter a new password.',
                // Thrown instead of auth/weak-password when the richer Identity
                // Platform password policy is enabled in the console.
                'auth/password-does-not-meet-requirements': 'That password does not meet the requirements. Please choose a longer, less common password.',
            };
            createPopup(
                friendlyMessages[error.code] || "Something went wrong. Please try again.",
                'error',
                { gradient: true, duration: 4000 }
            );

            // The code is single-use — once rejected, the form is dead.
            if (error.code === 'auth/expired-action-code'
                || error.code === 'auth/invalid-action-code'
                || error.code === 'auth/user-not-found') {
                setInvalidMessage(friendlyMessages[error.code]);
                setStatus('invalid');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
    <StyledEngineProvider injectFirst>
    <CssBaseline />
    <div className="auth-page">
        <div className="left-panel">
            <div className="logo-container">
                <Group31 className="group responsive-img" />
                <div>
                    <span className="joynet-text">JoyNet</span>
                </div>
            </div>

            <Typography variant="subtitle2" color="primary" className="tagline">
                WELCOME TO YOUR SPACE OF HEALING AND JOY
            </Typography>
            <Typography variant="h4" className="title-text">
                Choose a New Password
            </Typography>

            {status === 'checking' && (
                <Box className="signup-form">
                    <Typography variant="body2" className="sign-up-text">
                        Checking your reset link...
                    </Typography>
                </Box>
            )}

            {status === 'invalid' && (
                <Box className="signup-form">
                    {/* Not .sign-up-text: that class shifts by translateX(-7%), which
                        clips multi-line copy off the left edge on narrow screens. */}
                    <Typography variant="body2" align="center">
                        {invalidMessage}
                    </Typography>
                    <Typography variant="body2" className="sign-up-text">
                        <span className="login-link" onClick={() => navigate('/forgot-password')}>
                            Request a new reset link
                        </span>
                    </Typography>
                    <Typography variant="body2" className="sign-up-text">
                        Remembered it?&nbsp;
                        <span className="login-link" onClick={() => navigate('/login')}>
                            Log In
                        </span>
                    </Typography>
                </Box>
            )}

            {status === 'ready' && (
                <Box
                    component="form"
                    className="signup-form"
                    onSubmit={handleSubmit}
                >
                    {accountEmail && (
                        <TextField
                            name="accountEmail"
                            label="Email"
                            variant="filled"
                            value={accountEmail}
                            disabled
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Mail className="input-icon" />
                                    </InputAdornment>
                                ),
                                disableUnderline: true
                            }}
                        />
                    )}

                    <TextField
                        name="password"
                        label="New Password"
                        type={showPassword ? 'text' : 'password'}
                        variant="filled"
                        value={formData.password}
                        onChange={handleChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Briefcase className="input-icon" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowPassword}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                            disableUnderline: true
                        }}
                    />

                    <TextField
                        name="confirmPassword"
                        label="Confirm New Password"
                        type={showPassword ? 'text' : 'password'}
                        variant="filled"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Briefcase className="input-icon" />
                                </InputAdornment>
                            ),
                            disableUnderline: true
                        }}
                    />

                    {validationError && (
                        <Typography
                            variant="caption"
                            color="error"
                        >
                            {validationError}
                        </Typography>
                    )}

                    <Cta text={isSubmitting ? "Resetting..." : "Reset Password"} type="submit" disabled={isSubmitting} />

                    <Typography variant="body2" className="sign-up-text">
                        Changed your mind?&nbsp;
                        <span className="login-link" onClick={() => navigate('/login')}>
                            Log In
                        </span>
                    </Typography>
                </Box>
            )}
        </div>

        <div className="right-panel">
            <div className="benefit-card">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Check className="benefit-icon" />
                    <div>
                        <div className="benefit-title">Begin Your Journey to Healing</div>
                        <div className="benefit-description">
                            Find solace and connection in our community. The JoyNet App is a sanctuary where conversations heal and uplift.
                        </div>
                    </div>
                </div>
            </div>

            <div className="benefit-card">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Check className="benefit-icon" />
                    <div>
                        <div className="benefit-title">Simplicity in Connection, Richness in Experience</div>
                        <div className="benefit-description">
                            Signing up is just the start. Join chatrooms, connect with kindred spirits, and share in the celebration of Black Joy.
                        </div>
                    </div>
                </div>
            </div>
            <img src={maskGroup} alt="Illustration" className="auth-image" />
        </div>
    </div>
    </StyledEngineProvider>
    );
};

export default ResetPasswordPage;
